import { Grid } from "@mui/material";
import { ReactComponent as Priority } from "assets/Priority.svg";
import ThemeContext from "components/app/Context";
import { addToMyPlansAsync, updatePlanAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { SectionDetail } from "components/features/planDetails/PlanDetail";
import { useTranslate } from "hooks/useTranslate";
import * as React from "react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { ABOVE_PROPERTY } from "utils/CommonConstants";
import { converter_ArrayString, functionalAreaIdsToNames, getHltInncodes } from "utils/CommonFunctions";
import { formTypes } from "utils/Types";
import Input from "./Input";
import RichText from "./RichText";
import Calender from "./calender/Calender";
import DropDown from "./dropdowns/DropDown";
import MultipleSelectCheckmarks from "./dropdowns/MultiSelect";

type Inputs = {
  title: string;
  functionalArea: string;
  inncodes: string;
};

interface Props {
  item?: any;
  sectionPayload?: SectionDetail[];
  handleCloseDialog: () => void;
  copyToNewPlan?: boolean;
  showInncode?: boolean;
  disableInnCode?: boolean;
  formType?: string;
  isTemplateType?: boolean;
}

function Form(props: Props, ref: any) {
  const {
    item,
    sectionPayload = [],
    handleCloseDialog,
    copyToNewPlan = true,
    disableInnCode = true,
    formType = formTypes.CreatePlan,
    isTemplateType = false
  } = props;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<any>();

  const functionalAreaList: any = useAppSelector((state) => state.functionalAreas.list);
  const myPlanDetails: any = useAppSelector((state) => state.myPlanDetails.myPlanDetails);
  const innCode = useAppSelector((state: any) => state.app.innCode);
  const { isAbovePropertyUser } = useAppSelector((state) => state.app);
  const [searchParams] = useSearchParams();
  const [planTitle, setPlanTitle] = useState("");
  const [planFunctionalAreaIds, setPlanFunctionalAreaIds] = React.useState<string[]>(
    myPlanDetails?.functionalAreaIds || []
  );
  const [planDescription, setPlanDescription] = useState("");
  const [planPriority, setPlanPriority] = useState();
  const navigate = useNavigate();
  const [planInncode, setPlanInncode] = useState();
  const dispatch = useAppDispatch();
  const { isMobileScreen } = React.useContext(ThemeContext);
  const showField = formType === formTypes.CreatePlan || !myPlanDetails?.planType;
  const translate = useTranslate();

  const getDataRefreshTag = () => {
    if (
      (isTemplateType && searchParams.get("parentTab") === "Template Plans") ||
      (!isTemplateType && searchParams.get("parentTab") === "Property Plans")
    )
      return true;
    else return false;
  };

  const onSubmit = handleSubmit(async (data) => {
    let payload;
    handleCloseDialog();

    if (copyToNewPlan) {
      payload = {
        innCode: innCode,
        priority: planPriority,
        dueDate: data.dueDate,
        libraryPlan: {
          name: planTitle,
          summary: planDescription,
          functionalAreaIds: planFunctionalAreaIds,
          sections: sectionPayload?.length
            ? sectionPayload
            : [
                {
                  cards: item
                }
              ]
        }
      };
    } else {
      payload = {
        innCode: isTemplateType ? ABOVE_PROPERTY : planInncode ?? innCode,
        priority: planPriority,
        dueDate: data.dueDate,
        shouldDataRefresh: getDataRefreshTag(),
        libraryPlan: {
          name: planTitle,
          summary: planDescription,
          functionalAreaIds: planFunctionalAreaIds
        }
      };
    }
    const response = await dispatch(addToMyPlansAsync(payload));
    if (copyToNewPlan) navigate(`/myplandetails/${response?.payload?.id}`);
    else if (isAbovePropertyUser)
      navigate(
        `/myplans?parentTab=${isTemplateType ? translate("common.templatePlans") : translate("common.propertyPlans")}`
      );
    else if (!isAbovePropertyUser && !copyToNewPlan) navigate(`/myplans`);
  });

  const onSubmitEdit = handleSubmit((data) => {
    handleCloseDialog();
    let payload;

    if (myPlanDetails?.planType) {
      payload = {
        priority: planPriority ?? myPlanDetails?.priority,
        dueDate: data.dueDate
      };
    } else {
      payload = {
        priority: planPriority ?? myPlanDetails?.priority,
        dueDate: data.dueDate,
        title: planTitle || myPlanDetails?.name,
        description: planDescription || myPlanDetails?.summary,
        functionalAreas: planFunctionalAreaIds.length > 0 ? planFunctionalAreaIds : myPlanDetails?.functionalAreaIds
      };
    }

    dispatch(updatePlanAsync({ url: `${myPlanDetails?.id}`, payload }));
  });

  React.useImperativeHandle(ref, () => {
    return {
      submitForm: async () => {
        await onSubmit();
      },

      submitEditForm: () => {
        onSubmitEdit();
      }
    };
  });

  const getfunctionalAreas = (val: any) => {
    const areaDetails = val?.map((item: any) => functionalAreaList.find((FA: any) => item === FA.name).id);
    setPlanFunctionalAreaIds(areaDetails);
  };

  return (
    <Grid container margin="auto">
      <form style={{ width: "100%" }}>
        {showField && (
          <Grid item xs={12}>
            <Input
              label="Title"
              name="title"
              register={register}
              error={errors?.title}
              validations={{ required: true }}
              handleChangeUpdate={(title) => setPlanTitle(title)}
              textVariant="body2"
              placeholder="Add a brief title"
              sx={{ width: "100%" }}
              color={tokens.Text.General.PrimaryHeadline}
              value={formType === formTypes.EditPlan ? myPlanDetails?.name : ""}
            />
          </Grid>
        )}
        {showField && (
          <Grid item xs={12} display="flex" gap={1} marginTop="16px">
            {formType === formTypes.CreatePlan && !isTemplateType && (
              <Grid item xs={6}>
                {disableInnCode ? (
                  <DropDown label="Inncode" disabled={disableInnCode} initialValue={innCode} />
                ) : (
                  <DropDown
                    label="Inncode"
                    disabled={disableInnCode}
                    name="inncodes"
                    register={register}
                    error={errors?.inncodes}
                    validations={{ required: true }}
                    handleChangeUpdate={(id) => setPlanInncode(id)}
                    options={converter_ArrayString(getHltInncodes())}
                  />
                )}
              </Grid>
            )}
            <Grid item xs={isTemplateType ? 12 : 6}>
              <MultipleSelectCheckmarks
                isFormInput={true}
                minWidth="100%"
                name="functionalArea"
                register={register}
                error={errors?.functionalArea}
                validations={{ required: true }}
                label="Functional area"
                options={functionalAreaList}
                onChange={(val: any) => getfunctionalAreas(val)}
                initialValues={formType === formTypes.EditPlan && functionalAreaIdsToNames(planFunctionalAreaIds)}
              />
            </Grid>
          </Grid>
        )}
        {showField && (
          <Grid item xs={12} marginTop="16px">
            <RichText
              placeholder="Write the description"
              initialValue={formType === formTypes.EditPlan ? myPlanDetails?.summary : ""}
              handleChangeUpdate={(desc) => setPlanDescription(desc || null)}
              label="Description"
            />
          </Grid>
        )}
        {!isTemplateType && (
          <Grid item xs={12} display="flex" gap={1} marginTop="16px" flexWrap={isMobileScreen ? "wrap" : "nowrap"}>
            <Grid item xs={12} md={6}>
              <DropDown
                startAdornment={<Priority />}
                label="Priority"
                handleChangeUpdate={(val) => setPlanPriority(val || null)}
                options={converter_ArrayString(["Low", "Medium", "High"])}
                initialValue={formType === formTypes.EditPlan ? myPlanDetails.priority : ""}
                sx={{ margin: "4px 0px", "& .MuiSelect-select": { marginLeft: "11.43px" } }}
              />
            </Grid>
            <Grid item xs={12} md={6} marginTop="4px">
              <Controller
                name="dueDate"
                control={control}
                render={({ field: { onChange, name } }) => (
                  <Calender
                    onChange={onChange}
                    name={name}
                    error={errors?.dueDate}
                    defaultValue={formType === formTypes.EditPlan ? myPlanDetails?.dueDate : null}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </form>
    </Grid>
  );
}

export default React.forwardRef(Form);
